'use client'

import React, { useState, useEffect } from 'react'
import { BacktestResult } from '@/types'
import {
  BarChart3,
  Plus,
  Download,
  AlertCircle,
  Edit,
  Trash2
} from 'lucide-react'
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'
import { getBacktestResults, submitBacktestResult, updateBacktestResult, deleteBacktestResult } from '../lib/api'
import Toast from './Toast'
import ConfirmDialog from './ConfirmDialog'

interface BacktestResultsProps {
  strategyId: string
}

const BacktestResults: React.FC<BacktestResultsProps> = ({
  strategyId
}) => {
  const [selectedChart, setSelectedChart] = useState<'equity' | 'drawdown' | 'monthly'>('equity')
  const [backtestResults, setBacktestResults] = useState<BacktestResult[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [editingBacktest, setEditingBacktest] = useState<BacktestResult | null>(null)
  const [deletingBacktest, setDeletingBacktest] = useState<BacktestResult | null>(null)
  const [toast, setToast] = useState<{ type: 'success' | 'error', message: string } | null>(null)
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    total_return: '',
    max_drawdown: '',
    sharpe_ratio: '',
    win_rate: '',
    total_trades: '',
    notes: ''
  })

  useEffect(() => {
    fetchBacktestResults()
  }, [strategyId])

  const fetchBacktestResults = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getBacktestResults(strategyId)
      setBacktestResults(data)
    } catch (error) {
      console.error('获取回测结果失败:', error)
      setError('获取回测结果失败，请稍后重试')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitBacktest = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const backtestData = {
        strategy_id: strategyId,
        platform: 'TradingView',
        start_date: formData.start_date,
        end_date: formData.end_date,
        total_return: parseFloat(formData.total_return),
        max_drawdown: parseFloat(formData.max_drawdown),
        sharpe_ratio: parseFloat(formData.sharpe_ratio),
        win_rate: parseFloat(formData.win_rate),
        trade_count: parseInt(formData.total_trades),
        notes: formData.notes
      }

      await submitBacktestResult(backtestData)
      await fetchBacktestResults()
      setShowAddForm(false)
      setFormData({
        start_date: '',
        end_date: '',
        total_return: '',
        max_drawdown: '',
        sharpe_ratio: '',
        win_rate: '',
        total_trades: '',
        notes: ''
      })
      setToast({ type: 'success', message: '回测结果添加成功' })
    } catch (error) {
      console.error('提交回测结果失败:', error)
      setError('提交回测结果失败，请稍后重试')
      setToast({ type: 'error', message: '提交回测结果失败' })
    } finally {
      setLoading(false)
    }
  }

  const handleEditBacktest = (backtest: BacktestResult) => {
    setEditingBacktest(backtest)
    setFormData({
      start_date: backtest.start_date,
      end_date: backtest.end_date,
      total_return: backtest.total_return.toString(),
      max_drawdown: backtest.max_drawdown.toString(),
      sharpe_ratio: backtest.sharpe_ratio?.toString() || '',
      win_rate: backtest.win_rate.toString(),
      total_trades: backtest.trade_count.toString(),
      notes: backtest.notes || ''
    })
    setShowEditForm(true)
  }

  const handleUpdateBacktest = async () => {
    if (!editingBacktest) return

    try {
      setLoading(true)
      setError(null)
      
      const updates = {
        platform: 'TradingView',
        start_date: formData.start_date,
        end_date: formData.end_date,
        total_return: parseFloat(formData.total_return),
        max_drawdown: parseFloat(formData.max_drawdown),
        sharpe_ratio: parseFloat(formData.sharpe_ratio) || null,
        win_rate: parseFloat(formData.win_rate),
        trade_count: parseInt(formData.total_trades),
        notes: formData.notes || null
      }

      await updateBacktestResult(editingBacktest.id, updates)
      await fetchBacktestResults()
      setShowEditForm(false)
      setEditingBacktest(null)
      setFormData({
        start_date: '',
        end_date: '',
        total_return: '',
        max_drawdown: '',
        sharpe_ratio: '',
        win_rate: '',
        total_trades: '',
        notes: ''
      })
      setToast({ type: 'success', message: '回测结果更新成功' })
    } catch (error) {
      console.error('更新回测结果失败:', error)
      setError('更新回测结果失败，请稍后重试')
      setToast({ type: 'error', message: '更新回测结果失败' })
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteBacktest = async () => {
    if (!deletingBacktest) return

    try {
      setLoading(true)
      setError(null)
      
      await deleteBacktestResult(deletingBacktest.id)
      await fetchBacktestResults()
      setDeletingBacktest(null)
      setToast({ type: 'success', message: '回测结果删除成功' })
    } catch (error) {
      console.error('删除回测结果失败:', error)
      setError('删除回测结果失败，请稍后重试')
      setToast({ type: 'error', message: '删除回测结果失败' })
    } finally {
      setLoading(false)
    }
  }

  // 从回测结果生成图表数据
  const generateChartData = () => {
    if (!backtestResults.length) return []

    // 这里可以根据实际的回测数据结构来生成图表数据
    // 目前使用模拟数据，实际项目中应该从回测结果中提取
    return [
      { date: '2024-01', equity: 10000, drawdown: 0 },
      { date: '2024-02', equity: 10500, drawdown: -2 },
      { date: '2024-03', equity: 11200, drawdown: -1 },
      { date: '2024-04', equity: 10800, drawdown: -5 },
      { date: '2024-05', equity: 11500, drawdown: -3 },
      { date: '2024-06', equity: 12200, drawdown: -1 },
      { date: '2024-07', equity: 11800, drawdown: -4 },
      { date: '2024-08', equity: 12500, drawdown: -2 },
      { date: '2024-09', equity: 13200, drawdown: 0 },
      { date: '2024-10', equity: 12800, drawdown: -3 },
      { date: '2024-11', equity: 13500, drawdown: -1 },
      { date: '2024-12', equity: 14200, drawdown: 0 }
    ]
  }

  const generateMonthlyReturns = () => {
    if (!backtestResults.length) return []

    // 这里可以根据实际的回测数据结构来生成月度收益数据
    return [
      { month: '1月', return: 5.0 },
      { month: '2月', return: 6.7 },
      { month: '3月', return: -3.6 },
      { month: '4月', return: 6.5 },
      { month: '5月', return: 6.1 },
      { month: '6月', return: -3.3 },
      { month: '7月', return: 5.9 },
      { month: '8月', return: 5.9 },
      { month: '9月', return: 5.6 },
      { month: '10月', return: -3.0 },
      { month: '11月', return: 5.5 },
      { month: '12月', return: 5.2 }
    ]
  }

  const equityData = generateChartData()
  const monthlyReturns = generateMonthlyReturns()

  const formatPercentage = (value: number) => `${value.toFixed(2)}%`
  const formatNumber = (value: number) => value.toLocaleString()

  const renderChart = () => {
    switch (selectedChart) {
      case 'equity':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={equityData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
              <XAxis dataKey="date" stroke="#ffffff80" />
              <YAxis stroke="#ffffff80" />
              <Tooltip 
                formatter={(value: any) => [formatNumber(value), '资金']}
                labelFormatter={(label) => `日期: ${label}`}
                contentStyle={{
                  backgroundColor: 'rgba(0,0,0,0.8)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  color: '#fff'
                }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="equity" 
                stroke="#8b5cf6" 
                strokeWidth={3}
                name="资金曲线"
                dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )
      case 'drawdown':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={equityData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
              <XAxis dataKey="date" stroke="#ffffff80" />
              <YAxis stroke="#ffffff80" />
              <Tooltip 
                formatter={(value: any) => [formatPercentage(value), '回撤']}
                labelFormatter={(label) => `日期: ${label}`}
                contentStyle={{
                  backgroundColor: 'rgba(0,0,0,0.8)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  color: '#fff'
                }}
              />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="drawdown" 
                stroke="#ef4444" 
                fill="#ef444440"
                strokeWidth={2}
                name="回撤"
              />
            </AreaChart>
          </ResponsiveContainer>
        )
      case 'monthly':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={monthlyReturns}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
              <XAxis dataKey="month" stroke="#ffffff80" />
              <YAxis stroke="#ffffff80" />
              <Tooltip 
                formatter={(value: any) => [formatPercentage(value), '收益率']}
                labelFormatter={(label) => `月份: ${label}`}
                contentStyle={{
                  backgroundColor: 'rgba(0,0,0,0.8)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  color: '#fff'
                }}
              />
              <Legend />
              <Bar 
                dataKey="return" 
                fill="#10b981"
                name="月度收益"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        )
      default:
        return null
    }
  }

  return (
    <div className="space-y-6">
      {/* 错误提示 */}
      {error && (
        <div className="card bg-red-500/10 border border-red-500/30">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
            <span className="text-red-400">{error}</span>
          </div>
        </div>
      )}

      {/* 加载状态 */}
      {loading && (
        <div className="card">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
            <span className="ml-2 text-white/70">加载中...</span>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gradient">回测结果</h2>
        <div className="flex space-x-3">
          <button className="btn-secondary flex items-center whitespace-nowrap">
            <Download className="w-4 h-4 mr-2" />
            导出报告
          </button>
          <button 
            className="btn-primary flex items-center whitespace-nowrap"
            onClick={() => setShowAddForm(true)}
            disabled={loading}
          >
            <Plus className="w-4 h-4 mr-2" />
            新建回测
          </button>
        </div>
      </div>

      {/* 统计指标 */}
      {backtestResults.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-green-500/20 rounded-xl border border-green-500/30">
                <BarChart3 className="w-6 h-6 text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-white/70">总收益率</p>
                <p className="text-2xl font-bold text-gradient">
                  {formatPercentage(backtestResults[0].total_return)}
                </p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-red-500/20 rounded-xl border border-red-500/30">
                <BarChart3 className="w-6 h-6 text-red-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-white/70">最大回撤</p>
                <p className="text-2xl font-bold text-gradient">
                  {formatPercentage(backtestResults[0].max_drawdown)}
                </p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-blue-500/20 rounded-xl border border-blue-500/30">
                <BarChart3 className="w-6 h-6 text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-white/70">夏普比率</p>
                <p className="text-2xl font-bold text-gradient">
                  {backtestResults[0].sharpe_ratio?.toFixed(2) || 'N/A'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-500/20 rounded-xl border border-yellow-500/30">
                <BarChart3 className="w-6 h-6 text-yellow-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-white/70">胜率</p>
                <p className="text-2xl font-bold text-gradient">
                  {formatPercentage(backtestResults[0].win_rate)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 图表选择 */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">图表分析</h3>
          <div className="flex space-x-2">
            <button
              onClick={() => setSelectedChart('equity')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                selectedChart === 'equity'
                  ? 'bg-violet-500/20 text-white border border-violet-500/30'
                  : 'text-white/70 hover:bg-white/10'
              }`}
            >
              资金曲线
            </button>
            <button
              onClick={() => setSelectedChart('drawdown')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                selectedChart === 'drawdown'
                  ? 'bg-violet-500/20 text-white border border-violet-500/30'
                  : 'text-white/70 hover:bg-white/10'
              }`}
            >
              回撤分析
            </button>
            <button
              onClick={() => setSelectedChart('monthly')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                selectedChart === 'monthly'
                  ? 'bg-violet-500/20 text-white border border-violet-500/30'
                  : 'text-white/70 hover:bg-white/10'
              }`}
            >
              月度收益
            </button>
          </div>
        </div>
        
        <div className="bg-white/5 rounded-xl p-4">
          {renderChart()}
        </div>
      </div>

      {/* 回测详情 */}
      {backtestResults.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">回测详情</h3>
            <div className="flex space-x-2">
              <button
                onClick={() => handleEditBacktest(backtestResults[0])}
                className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                title="编辑回测结果"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={() => setDeletingBacktest(backtestResults[0])}
                className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                title="删除回测结果"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-white/70">回测平台</span>
                <span className="text-white">{backtestResults[0].platform}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/70">开始时间</span>
                <span className="text-white">
                  {new Date(backtestResults[0].start_date).toLocaleDateString('zh-CN')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/70">结束时间</span>
                <span className="text-white">
                  {new Date(backtestResults[0].end_date).toLocaleDateString('zh-CN')}
                </span>
              </div>
            </div>
            <div className="space-y-3">
                              <div className="flex justify-between">
                  <span className="text-white/70">交易次数</span>
                  <span className="text-white">{backtestResults[0].trade_count}</span>
                </div>
              <div className="flex justify-between">
                <span className="text-white/70">盈亏比</span>
                <span className="text-white">{backtestResults[0].profit_factor?.toFixed(2) || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/70">创建时间</span>
                <span className="text-white">
                  {new Date(backtestResults[0].created_at).toLocaleString('zh-CN')}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {!backtestResults.length && !loading && (
        <div className="empty-state">
          <BarChart3 className="w-16 h-16 text-white/30 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">暂无回测结果</h3>
          <p className="text-white/50 mb-6">开始您的第一次回测</p>
                    <button
            className="btn-primary flex items-center whitespace-nowrap"
            onClick={() => setShowAddForm(true)}
            data-add-backtest
          >
            <Plus className="w-4 h-4 mr-2" />
            新建回测
          </button>
        </div>
      )}

      {/* 添加回测结果表单 */}
      {showAddForm && (
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">添加回测结果</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                开始日期
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-violet-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                结束日期
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-violet-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                总收益率 (%)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.total_return}
                onChange={(e) => setFormData({...formData, total_return: e.target.value})}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-violet-500"
                placeholder="例如: 25.6"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                最大回撤 (%)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.max_drawdown}
                onChange={(e) => setFormData({...formData, max_drawdown: e.target.value})}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-violet-500"
                placeholder="例如: -8.2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                夏普比率
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.sharpe_ratio}
                onChange={(e) => setFormData({...formData, sharpe_ratio: e.target.value})}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-violet-500"
                placeholder="例如: 1.85"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                胜率 (%)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.win_rate}
                onChange={(e) => setFormData({...formData, win_rate: e.target.value})}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-violet-500"
                placeholder="例如: 68.5"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                交易次数
              </label>
              <input
                type="number"
                value={formData.total_trades}
                onChange={(e) => setFormData({...formData, total_trades: e.target.value})}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-violet-500"
                placeholder="例如: 45"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-white/70 mb-2">
                备注
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={3}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-violet-500"
                placeholder="添加回测相关的备注信息..."
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 text-white/70 hover:text-white transition-colors"
              disabled={loading}
            >
              取消
            </button>
            <button
              onClick={handleSubmitBacktest}
              disabled={loading}
              className="btn-primary flex items-center whitespace-nowrap"
            >
              {loading ? '提交中...' : '提交回测结果'}
            </button>
          </div>
        </div>
      )}

      {/* 编辑回测结果表单 */}
      {showEditForm && editingBacktest && (
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">编辑回测结果</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                开始日期
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-violet-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                结束日期
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-violet-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                总收益率 (%)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.total_return}
                onChange={(e) => setFormData({...formData, total_return: e.target.value})}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-violet-500"
                placeholder="例如: 25.6"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                最大回撤 (%)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.max_drawdown}
                onChange={(e) => setFormData({...formData, max_drawdown: e.target.value})}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-violet-500"
                placeholder="例如: -8.2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                夏普比率
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.sharpe_ratio}
                onChange={(e) => setFormData({...formData, sharpe_ratio: e.target.value})}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-violet-500"
                placeholder="例如: 1.85"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                胜率 (%)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.win_rate}
                onChange={(e) => setFormData({...formData, win_rate: e.target.value})}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-violet-500"
                placeholder="例如: 68.5"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                交易次数
              </label>
              <input
                type="number"
                value={formData.total_trades}
                onChange={(e) => setFormData({...formData, total_trades: e.target.value})}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-violet-500"
                placeholder="例如: 45"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-white/70 mb-2">
                备注
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={3}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-violet-500"
                placeholder="添加回测相关的备注信息..."
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setShowEditForm(false)}
              className="px-4 py-2 text-white/70 hover:text-white transition-colors"
              disabled={loading}
            >
              取消
            </button>
            <button
              onClick={handleUpdateBacktest}
              disabled={loading}
              className="btn-primary"
            >
              {loading ? '更新中...' : '更新回测结果'}
            </button>
          </div>
        </div>
      )}

             {/* 删除确认对话框 */}
       {deletingBacktest && (
         <ConfirmDialog
           isOpen={!!deletingBacktest}
           onCancel={() => setDeletingBacktest(null)}
           onConfirm={handleDeleteBacktest}
           title="确认删除"
           message={`确定要删除回测结果 "${deletingBacktest.total_return}%" 吗？此操作不可逆。`}
           confirmText="删除"
           cancelText="取消"
           type="danger"
         />
       )}

       {/* 提示信息 */}
       {toast && (
         <Toast
           message={toast.message}
           isVisible={!!toast}
           onClose={() => setToast(null)}
         />
       )}
    </div>
  )
}

export default BacktestResults 